/**
 * Tiny form primitives shared across all Config tabs. Keeps the tab files
 * focused on field layout rather than re-declaring the same Tailwind
 * classes on every input.
 */

import { forwardRef, useState } from 'react'

/** Decimals allowed; avoids `type="number"` step/spinner issues (Config QA 79–81). */
export const decimalTextInputAttrs = {
  type: 'text' as const,
  inputMode: 'decimal' as const,
}

/** Whole numbers; no spinner (Config QA 81). */
export const integerTextInputAttrs = {
  type: 'text' as const,
  inputMode: 'numeric' as const,
}
import { cn } from '@/lib/utils/helpers'
import { Eye, EyeOff } from 'lucide-react'
import type {
  ConfigLegacyInputProps,
  ConfigLegacyMaskedInputProps,
  ConfigLegacyTextAreaProps,
  SegmentedOption,
} from '../../types'

export type { SegmentedOption } from '../../types'

/** Red `*` for mandatory fields — matches the app-wide required-field convention. */
export function RequiredMark() {
  return <span className="text-red-500 ml-0.5">*</span>
}

export const LabeledInput = forwardRef<HTMLInputElement, ConfigLegacyInputProps>(
  ({ label, requiredMark, className, ...rest }, ref) => (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label}
        {requiredMark && <RequiredMark />}
      </label>
      <input
        ref={ref}
        {...rest}
        className={
          'w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ' +
          (className ?? '')
        }
      />
    </div>
  ),
)
LabeledInput.displayName = 'LabeledInput'

// Masked input with eye-toggle button. Mirrors Angular's
// `maskedFields[fieldName]` + toggleMask() pattern used for NINJA_*
// secrets in config.component.html.
export const MaskedInput = forwardRef<HTMLInputElement, ConfigLegacyMaskedInputProps>(
  ({ label, className, ...rest }, ref) => {
    const [masked, setMasked] = useState(true)
    return (
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
        <div className="relative">
          <input
            ref={ref}
            {...rest}
            type={masked ? 'password' : 'text'}
            className={
              'w-full px-3 py-2 pr-10 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ' +
              (className ?? '')
            }
          />
          <button
            type="button"
            onClick={() => setMasked((m) => !m)}
            title={masked ? 'Show' : 'Hide'}
            aria-label={masked ? 'Show value' : 'Hide value'}
            className="absolute inset-y-0 right-0 flex items-center pr-2 text-slate-500 hover:text-slate-700"
          >
            {masked ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
    )
  },
)
MaskedInput.displayName = 'MaskedInput'

export const LabeledTextArea = forwardRef<HTMLTextAreaElement, ConfigLegacyTextAreaProps>(
  ({ label, hint, className, ...rest }, ref) => (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {hint && <p className="text-[11px] text-slate-500 mb-1">{hint}</p>}
      <textarea
        ref={ref}
        {...rest}
        className={
          'w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ' +
          (className ?? '')
        }
      />
    </div>
  ),
)
LabeledTextArea.displayName = 'LabeledTextArea'

export function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-sm font-semibold text-slate-900 mb-2 mt-4 first:mt-0">
      {children}
    </h2>
  )
}

export function FieldLegend({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      <div className="flex flex-wrap gap-4">{children}</div>
    </div>
  )
}

/**
 * Yes/No and On/Off button pair — matches company Payments / property Config /
 * `PropertyExecutiveSummaryButtonGroup` (separate Liberty-blue pills, not a slate tray).
 */
export function SegmentedButtonGroup({
  ariaLabel,
  value,
  onChange,
  options,
  className,
}: {
  ariaLabel: string
  value: string
  onChange: (next: string) => void
  options: SegmentedOption[]
  className?: string
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn('inline-flex flex-wrap items-center gap-2.5', className)}
    >
      {options.map((opt) => {
        const selected = value === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(opt.value)}
            className={cn(
              'inline-flex min-h-[40px] min-w-[4.5rem] items-center justify-center rounded-lg border px-4 py-2 text-sm font-medium shadow-sm transition',
              selected
                ? 'border-[rgb(21_151_255)] bg-[rgb(21_151_255)] text-white shadow-[0_1px_3px_rgba(21,151,255,0.35)]'
                : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50',
            )}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}
